import React, { useState } from 'react';
import { X, Send, AlertCircle } from 'lucide-react';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type InquiryType = 'annonsering' | 'problem' | 'kontakt' | 'forfragan' | 'annat' | 'ny-funktion';

export function ContactModal({ isOpen, onClose }: ContactModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [inquiryTypes, setInquiryTypes] = useState<InquiryType[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Form validation
    if (!name || !email || !message) {
      setError('Vänligen fyll i alla obligatoriska fält.');
      setIsSubmitting(false);
      return;
    }

    // Get the form element
    const form = e.target as HTMLFormElement;
    
    // Create FormData object from the form
    const formData = new FormData(form);
    
    // Add the inquiry types as a comma-separated string
    if (inquiryTypes.length > 0) {
      formData.set('inquiry-types', inquiryTypes.join(','));
    }

    // Submit the form using Netlify's fetch API
    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData as any).toString()
    })
      .then(() => {
        setIsSubmitting(false);
        setIsSuccess(true);
        // Reset form after success
        setTimeout(() => {
          setName('');
          setEmail('');
          setMessage('');
          setInquiryTypes([]);
          setIsSuccess(false);
          onClose();
        }, 2000);
      })
      .catch((error) => {
        console.error('Form submission error:', error);
        setError('Ett fel uppstod. Försök igen senare.');
        setIsSubmitting(false);
      });
  };

  const toggleInquiryType = (type: InquiryType) => {
    if (inquiryTypes.includes(type)) {
      setInquiryTypes(inquiryTypes.filter(t => t !== type));
    } else {
      setInquiryTypes([...inquiryTypes, type]);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full relative transform transition-all">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
          aria-label="Stäng"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="p-6">
          <h2 className="text-2xl font-semibold mb-4 text-[#A3C1DA]">Kontakta oss</h2>
          
          {/* Success message */}
          {isSuccess && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4 flex items-center">
              <span className="mr-2">✓</span>
              Tack för ditt meddelande! Vi återkommer så snart som möjligt.
            </div>
          )}
          
          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 flex items-center">
              <AlertCircle className="w-4 h-4 mr-2" />
              {error}
            </div>
          )}
          
          {/* Contact form with Netlify integration */}
          <form
            className="space-y-4"
            onSubmit={handleSubmit}
            name="contact"
            method="POST"
            data-netlify="true"
            netlify-honeypot="bot-field"
            action="/success"
          >
            {/* Netlify hidden fields */}
            <input type="hidden" name="form-name" value="contact" />
            <div className="hidden">
              <label>
                Don't fill this out if you're human: <input name="bot-field" />
              </label>
            </div>

            {/* Name field */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Namn <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm p-2
                         focus:border-[#A3C1DA] focus:ring-[#A3C1DA]"
                required
                autoComplete="name"
              />
            </div>
            
            {/* Email field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                E-post <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm p-2
                         focus:border-[#A3C1DA] focus:ring-[#A3C1DA]"
                required
                autoComplete="email"
              />
            </div>
            
            {/* Inquiry type checkboxes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vad gäller din förfrågan?
              </label>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="type-annonsering"
                    name="inquiry-type[]"
                    value="annonsering"
                    checked={inquiryTypes.includes('annonsering')}
                    onChange={() => toggleInquiryType('annonsering')}
                    className="h-4 w-4 text-[#A3C1DA] focus:ring-[#A3C1DA] border-gray-300 rounded"
                  />
                  <label htmlFor="type-annonsering" className="ml-2 text-sm text-gray-700">
                    Annonsering
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="type-problem"
                    name="inquiry-type[]"
                    value="problem"
                    checked={inquiryTypes.includes('problem')}
                    onChange={() => toggleInquiryType('problem')}
                    className="h-4 w-4 text-[#A3C1DA] focus:ring-[#A3C1DA] border-gray-300 rounded"
                  />
                  <label htmlFor="type-problem" className="ml-2 text-sm text-gray-700">
                    Problem
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="type-kontakt"
                    name="inquiry-type[]"
                    value="kontakt"
                    checked={inquiryTypes.includes('kontakt')}
                    onChange={() => toggleInquiryType('kontakt')}
                    className="h-4 w-4 text-[#A3C1DA] focus:ring-[#A3C1DA] border-gray-300 rounded"
                  />
                  <label htmlFor="type-kontakt" className="ml-2 text-sm text-gray-700">
                    Kontakt
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="type-forfragan"
                    name="inquiry-type[]"
                    value="forfragan"
                    checked={inquiryTypes.includes('forfragan')}
                    onChange={() => toggleInquiryType('forfragan')}
                    className="h-4 w-4 text-[#A3C1DA] focus:ring-[#A3C1DA] border-gray-300 rounded"
                  />
                  <label htmlFor="type-forfragan" className="ml-2 text-sm text-gray-700">
                    Förfrågningar
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="type-annat"
                    name="inquiry-type[]"
                    value="annat"
                    checked={inquiryTypes.includes('annat')}
                    onChange={() => toggleInquiryType('annat')}
                    className="h-4 w-4 text-[#A3C1DA] focus:ring-[#A3C1DA] border-gray-300 rounded"
                  />
                  <label htmlFor="type-annat" className="ml-2 text-sm text-gray-700">
                    Annat
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="type-ny-funktion"
                    name="inquiry-type[]"
                    value="ny-funktion"
                    checked={inquiryTypes.includes('ny-funktion')}
                    onChange={() => toggleInquiryType('ny-funktion')}
                    className="h-4 w-4 text-[#A3C1DA] focus:ring-[#A3C1DA] border-gray-300 rounded"
                  />
                  <label htmlFor="type-ny-funktion" className="ml-2 text-sm text-gray-700">
                    Ide om ny funktion
                  </label>
                </div>
              </div>
            </div>
            
            {/* Message field */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                Meddelande <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm p-2
                         focus:border-[#A3C1DA] focus:ring-[#A3C1DA]"
                required
                autoComplete="off"
              ></textarea>
            </div>
            
            {/* Submit button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full bg-[#A3C1DA] text-white rounded-md py-3 px-4 
                       hover:bg-[#7A98B0] transition-colors flex items-center justify-center gap-2
                       ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isSubmitting ? 'Skickar...' : 'Skicka meddelande'}
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
